import $ from 'jquery';
import ShopifyVariants from 'shopify-variants';
import Forms from '../Forms';
import AddToCartFlyout from './AddToCartFlyout';
import ProductGallery from './ProductGallery';

export default class ProductDetails {
  constructor(options) {
    this.$window = $(window);
    this.$formArea = options.$formArea;
    this.$details = options.$details;
    this.context = options.context;
    this.settings = options.settings;
    this.product = options.product;
    this.useHistory = options.useHistory;
    this.sectionId = options.sectionId;
    this.initialVariant = options.initialVariant || null;

    this.variantHelper = null;
    this.addToCartFlyout = null;

    this.atcCallbacks = options.atcCallbacks;

    // Form
    this.$form = this.$formArea.find('[data-product-form]');
    this.$productAtcButton = this.$formArea.find('[data-product-atc]');
    this.$productVariants = this.$form.find('[data-variants]');
    this.$productOptions = this.$form.find('[data-product-option]');
    this.detailsLink = this.$formArea[0].querySelector('[data-product-details-link]');
    if (this.detailsLink) {
      this.detailsBaseHref = this.detailsLink.getAttribute('href');
    }

    this.variantFields = {
      $price: this.$details.find('[data-price]'),
      $priceMoney: this.$details.find('[data-price] .money'),
      $compareAtPrice: this.$details.find('[data-price-compare-at]'),
      $compareAtPriceMoney: this.$details.find('[data-price-compare-at] .money'),
      $badge: this.$details.find('[data-badge-sales]'),
      $badgeMoneySaved: this.$details.find('[data-price-money-saved]'),
      $badgePercentSaved: this.$details.find('[data-price-percent-saved]'),
      $sku: this.$details.find('[data-product-sku]'),
      unitPrice: this.$details[0].querySelector('[data-unit-price]'),
      totalQuantity: this.$details[0].querySelector('[data-total-quantity]'),
      unitPriceAmount: this.$details[0].querySelector('[data-unit-price-amount]'),
      unitPriceMeasure: this.$details[0].querySelector('[data-unit-price-measure]'),
      taxLine: this.$details[0].querySelector('[data-tax-line]'),
    };

    this.forms = new Forms(this.$form);

    if (this.product && 'media' in this.product) {
      this.gallery = new ProductGallery({
        el: options.gallery,
        settings: this.settings,
        isQuickshop: options.isQuickshop,
      });
    }

    this._bindEvents();
    this._initOptions();
  }

  unload() {
    this.$form.off(`.product-details-${this.sectionId}`);
    this.$window.off(`.product-details-${this.sectionId}`);
    this.forms.unload();

    if (this.variantHelper) {
      this.variantHelper.unload();
    }

    if (this.gallery) {
      this.gallery.unload();
    }
  }

  _bindEvents() {
    this.$window.on(`shopify-variants:switch-variant.product-details-${this.sectionId}`, (event, data) => this._switchVariant(event, data));
    this.$form.on(`submit.product-details-${this.sectionId}`, event => this._addToCartFlyout(event));
  }

  _initOptions() {
    if (!this.$productVariants.length) return;

    this.variantHelper = new ShopifyVariants(
      this.product,
      this.$productVariants,
      this.$productOptions,
      { mixedControls: true },
    );

    this.variantHelper.selectVariant(this.initialVariant);
  }

  _switchVariant(event, data) {
    if (data.product !== this.product) return;

    const {
      variant,
      firstLoad,
    } = data;

    if (firstLoad) {
      return;
    }

    // Update main select
    this.$productVariants.val(data.variant.id);

    if (this.gallery) {
      this.gallery.selectMediaByVariant(variant);
    }

    // Update Variant information
    this._updatePrice(variant);
    this._updateSKU(variant);
    this._updateBadge(variant);
    this._updateButton(variant);
    this._updateSwatchLabel(variant);
    this._updateFullDetailsLink(variant);
    this._updateUnitPrice(variant);

    if (this.useHistory) {
      const url = `${this.product.handle}?${$.param({ variant: variant.id })}`;
      history.replaceState({}, 'variant', url);
    }
  }

  _updatePrice(variant) {
    // Update compare at price
    const hasComparePrice = (
      !!variant.compare_at_price && variant.compare_at_price > variant.price
    );
    this.variantFields.$compareAtPrice.toggleClass('visible', hasComparePrice);
    this.variantFields.$compareAtPriceMoney.text(
      Shopify.formatMoney(variant.compare_at_price, this.settings.money_format),
    );

    // Update price
    this.variantFields.$priceMoney.text(
      Shopify.formatMoney(variant.price, this.settings.money_format),
    );
  }

  _updateSKU(variant) {
    if (variant.sku === '') {
      this.variantFields.$sku.parent().addClass('product-sku--empty');
    } else {
      this.variantFields.$sku.parent().removeClass('product-sku--empty');
    }

    this.variantFields.$sku.text(variant.sku);
  }

  _updateBadge(variant) {
    const priceSaved = variant.compare_at_price ? variant.compare_at_price - variant.price : 0;
    this.variantFields.$badge.toggle(!!priceSaved);

    if (this.variantFields.$badgeMoneySaved.length) {
      // Update badge if it shows money saved
      this.variantFields.$badgeMoneySaved.text(
        Shopify.formatMoney(priceSaved, this.settings.money_format),
      );
    }

    if (this.variantFields.$badgePercentSaved.length) {
      // Update badge if it shows percentiles
      const percentileSaved = Math.round((priceSaved * 100) / variant.compare_at_price);
      this.variantFields.$badgePercentSaved.text(percentileSaved);
    }
  }

  _updateButton(variant) {
    if (variant.available) {
      this.$productAtcButton.text(this.context.product_available);
      this.$productAtcButton
        .removeClass('disabled')
        .prop('disabled', false);
    } else {
      this.$productAtcButton.text(this.context.product_unavailable);
      this.$productAtcButton
        .addClass('disabled')
        .prop('disabled', true);
    }
  }

  _updateSwatchLabel(variant) {
    if (this.settings.swatches_enable) {
      const swatchLabel = this.$form[0].querySelector('[data-option-swatch-value]');
      if (swatchLabel) {
        swatchLabel.innerText = variant.options[parseInt(swatchLabel.dataset.optionSwatchValue, 10)];
      }
    }
  }

  _updateFullDetailsLink(variant) {
    if (this.detailsLink) {
      this.detailsLink.setAttribute('href', `${this.detailsBaseHref}?variant=${variant.id}`);
    }
  }

  _updateUnitPrice(variant) {
    if (this.variantFields.unitPrice && variant.unit_price_measurement) {
      this.variantFields.totalQuantity.innerHTML = `${variant.unit_price_measurement.quantity_value}${variant.unit_price_measurement.quantity_unit}`;

      this.variantFields.unitPriceAmount.innerHTML = Shopify.formatMoney(variant.unit_price, this.settings.money_format);
      if (variant.unit_price_measurement.reference_value === 1) {
        this.variantFields.unitPriceMeasure.innerHTML = variant.unit_price_measurement.reference_unit;
      } else {
        this.variantFields.unitPriceMeasure.innerHTML = `${variant.unit_price_measurement.reference_value}${variant.unit_price_measurement.reference_unit}`;
      }
      this.variantFields.unitPrice.classList.remove('hidden');
    } else if (this.variantFields.unitPrice) {
      this.variantFields.unitPrice.classList.add('hidden');
    }

    if (this.variantFields.taxLine) {
      if (variant.taxable) {
        this.variantFields.taxLine.classList.remove('hidden');
      } else {
        this.variantFields.taxLine.classList.add('hidden');
      }
    }
  }

  _addToCartFlyout(event) {
    event.preventDefault();

    if (this.addToCartFlyout) {
      this.addToCartFlyout.unload();
    }

    const formData = this.$form.serializeArray();

    const options = {
      atcButton: this.$productAtcButton[0],
      settings: {
        moneyFormat: this.settings.money_format,
        cartRedirection: this.settings.cart_redirection,
      },
    };

    this.addToCartFlyout = new AddToCartFlyout(formData, options, this.atcCallbacks);
  }
}
